import type { SortDescriptor } from '@react-types/shared';

import React from 'react';
import { motion } from 'framer-motion';
import { Table, TableBody, TableCell, TableColumn, TableHeader, TableRow } from '@heroui/react';
import { RiArrowDownSLine } from '@remixicon/react';

export type DataTableColumn = {
  name: React.ReactNode;
  uid: string;
  sortable?: boolean;
  align?: 'start' | 'center' | 'end';
  headerAlign?: 'start' | 'center' | 'end';
  headClass?: string;
};

export interface DataTableProps<TItem> {
  columns: DataTableColumn[];
  items: TItem[];
  renderCell: (item: TItem, columnKey: React.Key) => React.ReactNode;
  isLoading?: boolean;
  emptyContent?: React.ReactNode;
  loadingContent?: React.ReactNode;
  bottomContent?: React.ReactNode;
  minWidthPx?: number; // applied to inner wrapper for horizontal scroll
  selectionMode?: 'single' | 'multiple' | 'none';
  sortDescriptor?: SortDescriptor;
  onSortChange?: (d: SortDescriptor) => void;
  // Collapse functionality
  enableCollapse?: boolean;
  renderCollapseContent?: (item: TItem) => React.ReactNode;
}

export function DataTable<TItem>(props: DataTableProps<TItem>) {
  const {
    columns,
    items,
    renderCell,
    isLoading,
    emptyContent,
    loadingContent,
    bottomContent,
    minWidthPx = 1000,
    selectionMode = 'none',
    sortDescriptor,
    onSortChange,
    enableCollapse = false,
    renderCollapseContent
  } = props;

  const [selectedKeys, setSelectedKeys] = React.useState<Set<string | number>>(new Set());

  const handleRowClick = (itemId: string | number) => {
    setSelectedKeys((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(itemId)) {
        newSet.delete(itemId);
      } else {
        newSet.add(itemId);
      }
      return newSet;
    });
  };

  const isRowExpanded = (itemId: string | number) => selectedKeys.has(itemId);

  // Animated collapse using measured height to animate between 0 and content height
  const AnimatedCollapse: React.FC<{ expanded: boolean; children: React.ReactNode }> = ({ expanded, children }) => {
    const contentRef = React.useRef<HTMLDivElement | null>(null);
    const [targetHeight, setTargetHeight] = React.useState<number>(0);

    // Measure content height on mount/update
    React.useLayoutEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      setTargetHeight(el.scrollHeight);
    }, [children, expanded]);

    // Keep height up to date if content changes after expand
    React.useEffect(() => {
      const el = contentRef.current;
      if (!el) return;
      const ro = new ResizeObserver(() => {
        setTargetHeight(el.scrollHeight);
      });
      ro.observe(el);
      return () => ro.disconnect();
    }, []);

    return (
      <motion.div
        initial={false}
        animate={{ height: expanded ? targetHeight : 0, opacity: expanded ? 1 : 0 }}
        transition={{ duration: 0.22, ease: 'easeInOut' }}
        style={{ overflow: 'hidden' }}
      >
        <div ref={contentRef}>{children}</div>
      </motion.div>
    );
  };

  // Flatten items so TableBody renders exactly one TableRow per item.
  // We add a synthetic "details" row after each main row and control its visibility.
  const flattenedItems = React.useMemo(() => {
    return (items as unknown as Array<Record<string, unknown>>).flatMap((it) => {
      const id = (it as any).id as string | number;
      const mainKey = `${String(id)}-main`;
      const detailsKey = `${String(id)}-details`;
      return [
        { ...it, key: mainKey, __rowType: 'main' },
        { ...it, key: detailsKey, __rowType: 'details' }
      ];
    });
  }, [items]);

  // Force Table to re-evaluate rows when expanded state changes by embedding a hint on items
  const collectionItems = React.useMemo(() => {
    return flattenedItems.map((r: any) => ({
      ...r,
      __expanded: selectedKeys.has((r as any).id as string | number)
    }));
  }, [flattenedItems, selectedKeys]);

  // Keep both main and details rows in the collection for smoother animations;
  // details row animates its height between 0 and auto based on expanded state.

  return (
    <div className="h-full w-full overflow-x-auto pb-6">
      <div className={`min-w-[${minWidthPx}px] relative h-full`}>
        <Table
          isHeaderSticky
          removeWrapper
          isStriped
          aria-label="Data table"
          className="relative"
          classNames={{
            thead: '[&_tr]:border-b-none',
            th: 'px-2 py-2',
            td: 'px-2 py-2',
            tr: 'border-b border-default-200 first:border-t-0 last:border-b-0'
          }}
          color="primary"
          selectionMode={selectionMode}
          {...(sortDescriptor && { sortDescriptor })}
          {...(onSortChange && { onSortChange })}
        >
          <TableHeader columns={columns}>
            {(column: DataTableColumn) => {
              const columnIndex = columns.findIndex((col) => col.uid === column.uid);
              const headerClassName = columnIndex === 0 ? 'pl-6' : columnIndex === columns.length - 1 ? 'pr-6' : '';
              const headerAlign = column.headerAlign || column.align || 'start';

              // Map align to text alignment classes
              const alignClass =
                headerAlign === 'end' ? 'text-right' : headerAlign === 'center' ? 'text-center' : 'text-left';

              // Combine classes: headClass is added to default classes
              const defaultClasses = `${headerClassName} ${alignClass}`.trim();
              const finalClassName = column.headClass ? `${defaultClasses} ${column.headClass}`.trim() : defaultClasses;

              return (
                <TableColumn
                  key={column.uid}
                  align={headerAlign}
                  allowsSorting={Boolean(column.sortable)}
                  className={finalClassName}
                >
                  {column.name}
                </TableColumn>
              );
            }}
          </TableHeader>
          <TableBody
            emptyContent={emptyContent}
            isLoading={isLoading}
            items={collectionItems as unknown as TItem[]}
            loadingContent={loadingContent}
          >
            {(row: any) => {
              const rowType = (row as any).__rowType as 'main' | 'details' | undefined;
              const itemId = (row as any).id as string | number;
              const expanded = (row as any).__expanded ?? isRowExpanded(itemId);

              if (rowType === 'details') {
                // Render details row as a single TableRow with a single spanning cell
                return (
                  <TableRow key={String((row as any).key ?? `${String(itemId)}-details`)}>
                    <TableCell colSpan={columns.length} className="p-0">
                      <AnimatedCollapse expanded={Boolean(enableCollapse && renderCollapseContent && expanded)}>
                        <div className="border-primary mx-1.5 border-l-2 bg-gray-100 p-4 dark:border-gray-700 dark:bg-gray-900">
                          {renderCollapseContent ? renderCollapseContent(row as TItem) : null}
                        </div>
                      </AnimatedCollapse>
                    </TableCell>
                  </TableRow>
                );
              }

              // Default: main row
              return (
                <TableRow
                  key={String((row as any).key ?? `${String(itemId)}-main`)}
                  className={
                    enableCollapse
                      ? `${expanded ? 'bg-default-100 dark:bg-default-100/10' : ''} hover:bg-gray-50 dark:hover:bg-gray-800`
                      : ''
                  }
                >
                  {(columnKey) => {
                    const columnIndex = columns.findIndex((col) => col.uid === columnKey);
                    const cellClassName = columnIndex === 0 ? 'pl-3' : columnIndex === columns.length - 1 ? 'pr-3' : '';

                    if (enableCollapse && columnKey === columns[0]?.uid) {
                      return (
                        <TableCell className={cellClassName}>
                          <div className="flex items-center gap-2">
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                handleRowClick(itemId);
                              }}
                              className="flex h-6 w-6 items-center justify-center rounded transition-colors hover:bg-gray-100 dark:hover:bg-gray-800"
                              aria-label={expanded ? 'Collapse' : 'Expand'}
                            >
                              <RiArrowDownSLine
                                className={`h-4 w-4 transition-transform ${expanded ? 'rotate-180' : ''}`}
                              />
                            </button>
                            {renderCell(row as TItem, columnKey)}
                          </div>
                        </TableCell>
                      );
                    }
                    return <TableCell className={cellClassName}>{renderCell(row as TItem, columnKey)}</TableCell>;
                  }}
                </TableRow>
              );
            }}
          </TableBody>
        </Table>
        {bottomContent && <div className="mt-4 flex w-full justify-center">{bottomContent}</div>}
      </div>
    </div>
  );
}

export default DataTable;
